/**
 * Desk — Today (the moat): the user's hand-ordered plan for a date, built from
 * cards across all projects. Checkbox syncs card status (check → done,
 * uncheck → open when closed). Unfinished entries never auto-migrate — the
 * carry-over banner makes it an explicit action, so history stays honest.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Heading } from "catalyst-ui-kit/heading";
import { Text } from "catalyst-ui-kit/text";
import { Badge } from "catalyst-ui-kit/badge";
import DeskNav from "components/desk-nav/desk-nav";
import DatePicker from "features/datepicker/date-picker";
import DeskCardDetailPanel from "components/desk-card-detail-panel/desk-card-detail-panel";
import {
  fetchDeskDayPlan,
  saveDeskDayPlanOrder,
  addDeskDayPlanEntry,
  updateDeskDayPlanEntry,
  removeDeskDayPlanEntry,
  setDeskDayCapacity,
} from "services/desk.service";
import { DeskDayPlanEntryItem, DeskCarryOverItem, DeskDayCapacityInfo } from "types/Desk";

/** desk_day_plan_entries.note is VARCHAR(300) — longer text used to vanish. */
const NOTE_MAX = 300;

function entryPoints(entry: DeskDayPlanEntryItem): number {
  if (entry.points !== null && entry.points !== undefined) return entry.points;
  return entry.card?.estimatePoints || 0;
}

/** Server message first — it names the actual reason (INVALID_POINTS, 403…). */
function errorMessage(e: any): string {
  return e?.response?.data?.message || e?.message || "";
}

const STATUS_COLOR: Record<string, "lime" | "amber" | "zinc"> = {
  open: "lime",
  waiting_client: "amber",
  done: "zinc",
};

function isoDate(d: Date): string {
  return d.toLocaleDateString("sv"); // YYYY-MM-DD in local time
}

function shiftDate(date: string, days: number): string {
  const d = new Date(date + "T12:00:00");
  d.setDate(d.getDate() + days);
  return isoDate(d);
}

// 44px on a phone: the two arrows used to be 24px squares stacked 4px apart.
const arrowBtnCls =
  "flex h-6 w-6 max-md:h-11 max-md:w-11 items-center justify-center rounded-md border border-zinc-200 bg-zinc-50 text-[10px] leading-none text-zinc-600 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-30 disabled:hover:border-zinc-200 disabled:hover:bg-zinc-50 disabled:hover:text-zinc-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:border-indigo-900 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-400 dark:disabled:hover:border-zinc-800 dark:disabled:hover:bg-zinc-900 dark:disabled:hover:text-zinc-400 transition-colors";

export default function DeskToday() {
  const { companyId } = useParams<{ companyId: string }>();
  const { t, i18n } = useTranslation();
  const today = isoDate(new Date());
  const [date, setDate] = useState(today);
  const [entries, setEntries] = useState<DeskDayPlanEntryItem[]>([]);
  const [carryOver, setCarryOver] = useState<DeskCarryOverItem[]>([]);
  const [carryOverTotal, setCarryOverTotal] = useState(0);
  const [carryBusy, setCarryBusy] = useState(false);
  const [capacity, setCapacity] = useState<DeskDayCapacityInfo | null>(null);
  const [capacityEdit, setCapacityEdit] = useState<string | null>(null);
  const [capacityError, setCapacityError] = useState<string | null>(null);
  const [pointsEditId, setPointsEditId] = useState<string | null>(null);
  const [pointsEdit, setPointsEdit] = useState("");
  const [addNumber, setAddNumber] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // null = loaded fine; "" = failed without a message from the server
  const [error, setError] = useState<string | null>(null);
  const [noteEdits, setNoteEdits] = useState<Record<string, string>>({});
  const [activeCardNumber, setActiveCardNumber] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const data = await fetchDeskDayPlan(companyId, date);
      setEntries(data.entries || []);
      setCarryOver(data.carryOver || []);
      setCarryOverTotal(data.carryOverTotal ?? (data.carryOver || []).length);
      setCapacity(data.capacity || null);
      setCapacityEdit(null);
      setCapacityError(null);
      const notes: Record<string, string> = {};
      (data.entries || []).forEach((e: DeskDayPlanEntryItem) => {
        notes[e.id] = e.note || "";
      });
      setNoteEdits(notes);
      setError(null);
    } catch (e: any) {
      setError(errorMessage(e));
    } finally {
      setIsLoading(false);
    }
  }, [companyId, date]);

  useEffect(() => {
    // wipe first: otherwise the previous day's rows stay on screen while the new
    // date loads, and a "done" click lands on an entry of another day
    setIsLoading(true);
    setEntries([]);
    setCarryOver([]);
    setCarryOverTotal(0);
    setCapacity(null);
    setNoteEdits({});
    setPointsEditId(null);
    load();
  }, [load]);

  const move = async (index: number, delta: number) => {
    if (!companyId) return;
    const next = entries.slice();
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setEntries(next);
    try {
      await saveDeskDayPlanOrder(companyId, date, next.map((e) => e.id));
    } catch (e) {
      toast.error(errorMessage(e) || t("desk_action_failed"));
      load();
    }
  };

  const toggleDone = async (entry: DeskDayPlanEntryItem) => {
    if (!companyId) return;
    const nextDone = !entry.doneAt;
    setEntries((list) =>
      list.map((e) =>
        e.id === entry.id
          ? {
              ...e,
              doneAt: nextDone ? Date.now() : null,
              card: e.card
                ? {
                    ...e.card,
                    // Uncheck only flips open when the card was done (API same).
                    status: nextDone
                      ? "done"
                      : e.card.status === "done"
                        ? "open"
                        : e.card.status,
                  }
                : e.card,
            }
          : e
      )
    );
    try {
      await updateDeskDayPlanEntry(companyId, entry.id, { done: nextDone });
    } catch (e) {
      toast.error(errorMessage(e) || t("desk_action_failed"));
      load();
    }
  };

  const saveNote = async (entry: DeskDayPlanEntryItem) => {
    if (!companyId) return;
    const note = noteEdits[entry.id] || "";
    if (note === (entry.note || "")) return;
    try {
      await updateDeskDayPlanEntry(companyId, entry.id, { note });
      // mirror the saved value onto the row, so the dirty check above compares
      // against what the server holds — without this a cleared note is dirty
      // forever on the client and comes back on the next load
      setEntries((list) => list.map((e) => (e.id === entry.id ? { ...e, note: note || null } : e)));
    } catch (e) {
      // no load() here: it would overwrite noteEdits and lose what was typed
      toast.error(errorMessage(e) || t("desk_action_failed"));
    }
  };

  const savePoints = async (entry: DeskDayPlanEntryItem) => {
    if (!companyId) return;
    const raw = pointsEdit.trim();
    setPointsEditId(null);
    const points = raw === "" ? null : Number(raw);
    if (points !== null && (!Number.isFinite(points) || points < 0 || points > 100)) {
      toast.error(t("desk_invalid_points"));
      return;
    }
    if ((entry.points ?? null) === points) return;
    setEntries((list) => list.map((e) => (e.id === entry.id ? { ...e, points } : e)));
    try {
      // per-entry override of the card estimate; empty falls back to the card
      await updateDeskDayPlanEntry(companyId, entry.id, { points });
    } catch (e) {
      toast.error(errorMessage(e) || t("desk_action_failed"));
      load();
    }
  };

  const remove = async (entry: DeskDayPlanEntryItem) => {
    if (!companyId) return;
    setEntries((list) => list.filter((e) => e.id !== entry.id));
    try {
      await removeDeskDayPlanEntry(companyId, entry.id);
    } catch (e) {
      toast.error(errorMessage(e) || t("desk_action_failed"));
      load();
    }
  };

  const addByNumber = async () => {
    if (!companyId) return;
    const number = parseInt(addNumber, 10);
    if (!Number.isFinite(number) || number <= 0) return;
    setAddBusy(true);
    try {
      await addDeskDayPlanEntry(companyId, date, { cardNumber: number });
      setAddNumber("");
      await load();
      toast.success(t("desk_added_to_plan", { number: number }));
    } catch (e) {
      toast.error(errorMessage(e) || t("desk_action_failed"));
    } finally {
      setAddBusy(false);
    }
  };

  const carryOverOne = async (item: DeskCarryOverItem) => {
    if (!companyId) return;
    setCarryBusy(true);
    try {
      await addDeskDayPlanEntry(companyId, date, {
        cardNumber: item.card.number,
        note: item.note || undefined,
      });
      await load();
    } catch (e) {
      toast.error(errorMessage(e) || t("desk_action_failed"));
    } finally {
      setCarryBusy(false);
    }
  };

  const carryOverAll = async () => {
    if (!companyId || !carryOver.length) return;
    setCarryBusy(true);
    const failed: number[] = [];
    for (const item of carryOver) {
      // sequential to keep positions stable; one reload at the end, not per item
      try {
        // eslint-disable-next-line no-await-in-loop
        await addDeskDayPlanEntry(companyId, date, {
          cardNumber: item.card.number,
          note: item.note || undefined,
        });
      } catch (e) {
        failed.push(item.card.number);
      }
    }
    await load();
    setCarryBusy(false);
    if (failed.length) {
      toast.error(
        t("desk_carry_over_failed", { numbers: failed.map((n) => "#" + n).join(", ") })
      );
    } else {
      toast.success(t("desk_carry_over_done", { count: carryOver.length }));
    }
  };

  const doneCount = entries.filter((e) => e.doneAt).length;
  const waitingCount = entries.filter((e) => e.card && e.card.status === "waiting_client").length;
  const plannedPoints = entries.reduce((sum, e) => sum + entryPoints(e), 0);
  const donePoints = entries.filter((e) => e.doneAt).reduce((sum, e) => sum + entryPoints(e), 0);
  const capacityPoints = capacity ? capacity.points : 0;
  // capacity 0 (day off) + anything planned IS over capacity
  const overCapacity = plannedPoints > capacityPoints;
  const weekday = new Date(date + "T12:00:00").toLocaleDateString(
    (i18n as any).language || "lt",
    { weekday: "short" }
  );

  const barWidth = (points: number) => {
    if (capacityPoints > 0) return Math.min(100, (points / capacityPoints) * 100) + "%";
    return points > 0 ? "100%" : "0%";
  };

  const saveCapacity = async () => {
    if (!companyId || capacityEdit === null) return;
    const raw = capacityEdit.trim();
    const points = Number(raw);
    if (raw === "" || !Number.isFinite(points) || points < 0 || points > 100) {
      // stay put instead of the old silent return that left the junk in the field
      setCapacityError(t("desk_invalid_capacity"));
      setCapacityEdit(String(capacityPoints));
      return;
    }
    setCapacityError(null);
    try {
      await setDeskDayCapacity(companyId, date, points);
      await load();
    } catch (e) {
      setCapacityError(errorMessage(e) || t("desk_action_failed"));
      setCapacityEdit(String(capacityPoints));
    }
  };

  const resetCapacity = async () => {
    if (!companyId) return;
    setCapacityError(null);
    try {
      await setDeskDayCapacity(companyId, date, null);
      await load();
    } catch (e) {
      setCapacityError(errorMessage(e) || t("desk_action_failed"));
    }
  };

  return (
    <div className="mx-auto max-w-full p-4 md:p-6">
      <DeskNav />
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-100 pb-4 dark:border-zinc-900">
        <Heading className="!font-bold">{t("desk_today")}</Heading>
        <div className="flex flex-wrap items-center gap-1 bg-zinc-50 border border-zinc-200/80 p-1 rounded-xl dark:bg-zinc-900 dark:border-zinc-800 text-sm">
          <button
            type="button"
            onClick={() => setDate(shiftDate(date, -1))}
            className="h-8 w-8 max-md:h-11 max-md:w-11 rounded-lg flex items-center justify-center font-bold hover:bg-zinc-200/50 dark:hover:bg-zinc-800 transition-colors"
            aria-label={t("desk_prev_day")}
            title={t("desk_prev_day")}
          >
            ←
          </button>
          <div className="desk-today-datepicker-wrapper">
            <DatePicker
              selected={date}
              onChange={(d: Date | null) => {
                if (d) {
                  // the picker hands back a UTC-midnight date, so the UTC parts
                  // are the picked day in every timezone
                  setDate(d.toISOString().split("T")[0]);
                }
              }}
              title={t("desk_plan_date")}
              fullWidth={false}
              disableDatesAfter={false}
            />
          </div>
          <button
            type="button"
            onClick={() => setDate(shiftDate(date, 1))}
            className="h-8 w-8 max-md:h-11 max-md:w-11 rounded-lg flex items-center justify-center font-bold hover:bg-zinc-200/50 dark:hover:bg-zinc-800 transition-colors"
            aria-label={t("desk_next_day")}
            title={t("desk_next_day")}
          >
            →
          </button>
          <span className="px-1.5 text-xs font-semibold uppercase tracking-wide text-zinc-500">
            {weekday}
          </span>
          {date !== today && (
            <button
              type="button"
              onClick={() => setDate(today)}
              className="px-3 py-1 max-md:min-h-11 rounded-lg bg-indigo-50 text-indigo-600 font-semibold hover:bg-indigo-100 dark:bg-indigo-950/40 dark:text-indigo-400 dark:hover:bg-indigo-950/60 transition-colors ml-1"
            >
              {t("desk_today")}
            </button>
          )}
        </div>
      </div>

      {error !== null && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-red-500/10 p-3.5 border border-red-500/20">
          <Text className="!text-red-700 dark:!text-red-400 font-medium">
            {error || t("desk_load_failed")}
          </Text>
          <button
            type="button"
            onClick={() => {
              setIsLoading(true);
              load();
            }}
            className="rounded-lg bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-500/20 dark:text-red-400 dark:hover:bg-red-500/20 transition-colors max-md:min-h-11"
          >
            {t("retry")}
          </button>
        </div>
      )}

      {/* Capacity bar: planned points vs the day's capacity (default 4) */}
      {capacity && (
        <div className="mt-5 rounded-2xl border border-zinc-200/80 bg-white p-5 shadow-sm dark:border-zinc-800 dark:bg-zinc-950 transition-all">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm font-medium">
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="text-zinc-500">{t("desk_points")}:</span>
              <strong className="text-zinc-900 dark:text-zinc-100 text-base">{plannedPoints}</strong>
              <span className="text-zinc-400">/</span>
              {capacityEdit !== null ? (
                <input
                  autoFocus
                  type="number"
                  min={0}
                  max={100}
                  value={capacityEdit}
                  onChange={(e) => setCapacityEdit(e.target.value)}
                  onBlur={saveCapacity}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveCapacity();
                    if (e.key === "Escape") {
                      setCapacityEdit(null);
                      setCapacityError(null);
                    }
                  }}
                  aria-label={t("desk_edit_capacity")}
                  className="w-16 rounded-lg border border-zinc-300 bg-transparent px-2 py-0.5 text-center font-semibold max-md:min-h-11 max-md:text-base focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:border-zinc-700 focus:outline-none"
                />
              ) : (
                <span className="flex items-center gap-1 text-zinc-700 dark:text-zinc-300">
                  {capacityPoints}
                  <button
                    type="button"
                    onClick={() => setCapacityEdit(String(capacityPoints))}
                    className="h-5 w-5 max-md:-my-3 max-md:h-11 max-md:w-11 flex items-center justify-center rounded text-zinc-400 hover:text-indigo-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all"
                    title={t("desk_edit_capacity")}
                    aria-label={t("desk_edit_capacity")}
                  >
                    ✎
                  </button>
                </span>
              )}
              {/* an override is invisible otherwise — you cannot tell it from the default, nor undo it */}
              {capacity.source === "override" && (
                <span className="flex items-center gap-1">
                  <span
                    className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20"
                    title={capacity.note || undefined}
                  >
                    {t("desk_capacity_override")}
                  </span>
                  <button
                    type="button"
                    onClick={resetCapacity}
                    className="h-5 w-5 max-md:-my-3 max-md:h-11 max-md:w-11 flex items-center justify-center rounded text-zinc-400 hover:text-indigo-500 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-all"
                    title={t("desk_capacity_reset")}
                    aria-label={t("desk_capacity_reset")}
                  >
                    ↺
                  </button>
                </span>
              )}
              {overCapacity && (
                <span className="ml-2 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider rounded-md bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20">
                  {t("desk_over_capacity")}
                </span>
              )}
            </span>
            <span className="text-zinc-500">
              {donePoints} {t("desk_done_lc")}
            </span>
          </div>
          {capacityError && (
            <div className="mt-2 text-xs font-medium text-red-600 dark:text-red-400">{capacityError}</div>
          )}
          <div
            className="relative mt-3 h-2.5 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-900"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={capacityPoints || plannedPoints || 1}
            aria-valuenow={plannedPoints}
            aria-label={t("desk_points")}
          >
            <div
              className={`h-full rounded-full transition-all duration-300 ${overCapacity ? "bg-gradient-to-r from-red-500 to-amber-500" : "bg-gradient-to-r from-emerald-500 to-teal-500"}`}
              style={{ width: barWidth(plannedPoints) }}
            />
            {/* darker head = the part actually finished, which is what the label counts */}
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-emerald-700/70 transition-all duration-300 dark:bg-emerald-300/60"
              style={{ width: barWidth(donePoints) }}
            />
          </div>
        </div>
      )}

      {carryOver.length > 0 && (
        <div className="mt-5 rounded-2xl border border-amber-300/40 bg-amber-500/5 p-5 shadow-sm dark:border-amber-900/30">
          <div className="flex items-center justify-between gap-2 border-b border-amber-300/20 pb-2 mb-3">
            <span className="font-semibold text-amber-800 dark:text-amber-400 flex items-center gap-1.5 text-sm">
              <span>⚠️</span> {t("desk_carry_over")}:{" "}
              {carryOverTotal > carryOver.length
                ? carryOver.length + " / " + carryOverTotal
                : carryOver.length}
            </span>
            <button
              type="button"
              onClick={carryOverAll}
              disabled={carryBusy}
              className="rounded-lg bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-500/20 disabled:opacity-50 dark:text-amber-400 dark:bg-amber-500/20 dark:hover:bg-amber-500/30 transition-colors max-md:min-h-11"
            >
              {t("desk_carry_over_all")}
            </button>
          </div>
          <div className="space-y-2 text-xs">
            {carryOver.map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-3 bg-white/40 dark:bg-zinc-950/20 p-2.5 rounded-lg border border-amber-300/20">
                <button
                  type="button"
                  onClick={() => setActiveCardNumber(item.card.number)}
                  className="min-w-0 flex-1 truncate rounded text-left text-zinc-700 dark:text-zinc-300 font-medium hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
                >
                  <span className="text-zinc-500 mr-1.5 font-mono">
                    {item.planDate} · #{item.card.number}
                  </span>{" "}
                  {item.card.title}
                </button>
                <button
                  type="button"
                  onClick={() => carryOverOne(item)}
                  disabled={carryBusy}
                  className="shrink-0 h-6 w-6 max-md:h-11 max-md:w-11 rounded-md bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 disabled:opacity-50 dark:text-amber-400 dark:bg-amber-500/20 dark:hover:bg-amber-500/30 flex items-center justify-center font-bold transition-colors"
                  title={t("desk_carry_over_one")}
                  aria-label={t("desk_carry_over_one")}
                >
                  →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Today has to be reachable on its own — not only through the Inbox shortcut */}
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <label htmlFor="desk-plan-add-number" className="text-xs font-medium text-zinc-500">
          {t("desk_add_to_plan")}
        </label>
        <input
          id="desk-plan-add-number"
          type="number"
          min={1}
          value={addNumber}
          onChange={(e) => setAddNumber(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addByNumber()}
          placeholder={t("desk_card_number")}
          aria-label={t("desk_card_number")}
          className="w-28 rounded-lg border border-zinc-200/80 bg-transparent px-2.5 py-1.5 text-xs max-md:min-h-11 max-md:text-base focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:border-zinc-800 focus:outline-none transition-all"
        />
        <button
          type="button"
          onClick={addByNumber}
          disabled={addBusy || !addNumber.trim()}
          className="rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-100 disabled:opacity-50 dark:bg-indigo-950/40 dark:text-indigo-400 dark:hover:bg-indigo-950/60 transition-colors max-md:min-h-11"
        >
          {t("add")}
        </button>
      </div>

      {isLoading && (
        <div className="mt-5 space-y-3 animate-pulse" aria-hidden="true">
          <div className="h-20 rounded-2xl bg-zinc-100 dark:bg-zinc-900" />
          <div className="h-20 rounded-2xl bg-zinc-100 dark:bg-zinc-900" />
          <div className="h-20 rounded-2xl bg-zinc-100 dark:bg-zinc-900" />
        </div>
      )}

      {!isLoading && error === null && !entries.length && (
        <div className="mt-6 rounded-2xl bg-zinc-50 border border-zinc-200/50 p-8 text-center dark:bg-zinc-900/20 dark:border-zinc-800">
          <Text className="text-zinc-500">{t("desk_no_plan")}</Text>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {entries.map((entry, index) => (
          <div
            key={entry.id}
            className="flex flex-wrap items-center gap-3.5 rounded-2xl border border-zinc-200/80 bg-white p-4 shadow-sm hover:shadow transition-all duration-150 dark:border-zinc-800 dark:bg-zinc-950/80 max-md:gap-x-2.5 max-md:gap-y-1 max-md:p-3"
          >
            {/* On a phone the row reads in two lines (CSS `order`): tick, title and ✕;
                then the arrows (side by side) and the note. The full-width spacer
                is what pushes the second line down. */}
            <div aria-hidden="true" className="hidden max-md:order-4 max-md:block max-md:basis-full" />
            <div className="flex flex-col gap-1 max-md:order-5 max-md:flex-row">
              <button
                type="button"
                onClick={() => move(index, -1)}
                disabled={index === 0}
                className={arrowBtnCls}
                title={t("desk_move_up")}
                aria-label={t("desk_move_up")}
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => move(index, 1)}
                disabled={index === entries.length - 1}
                className={arrowBtnCls}
                title={t("desk_move_down")}
                aria-label={t("desk_move_down")}
              >
                ▼
              </button>
            </div>
            <label className="flex shrink-0 items-center max-md:order-1 max-md:size-11 max-md:justify-center">
              <input
                type="checkbox"
                checked={Boolean(entry.doneAt)}
                onChange={() => toggleDone(entry)}
                aria-label={t("desk_mark_done")}
                className="size-5 shrink-0 rounded border-zinc-300 text-indigo-600 focus:ring-indigo-500 accent-indigo-600 cursor-pointer"
              />
            </label>
            <div className="min-w-0 flex-1 max-md:order-2">
              <div className="break-words font-medium text-zinc-900 dark:text-zinc-100 md:truncate">
                {entry.card ? (
                  <button
                    type="button"
                    onClick={() => entry.card && setActiveCardNumber(entry.card.number)}
                    className={`rounded hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline cursor-pointer text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 ${entry.doneAt ? "line-through opacity-45" : ""}`}
                  >
                    <span className="text-zinc-400 font-mono mr-1">#{entry.card.number}</span> {entry.card.title}
                  </button>
                ) : (
                  "—"
                )}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                {/* points are per-entry: the same card can cost 3 today and 5 tomorrow */}
                {pointsEditId === entry.id ? (
                  <input
                    autoFocus
                    type="number"
                    min={0}
                    max={100}
                    value={pointsEdit}
                    onChange={(e) => setPointsEdit(e.target.value)}
                    onBlur={() => savePoints(entry)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                      if (e.key === "Escape") setPointsEditId(null);
                    }}
                    placeholder={String(entry.card?.estimatePoints || 0)}
                    aria-label={t("desk_entry_points")}
                    className="w-16 rounded border border-zinc-300 bg-transparent px-1.5 py-0.5 text-[11px] text-center max-md:min-h-11 max-md:text-base focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:border-zinc-700 focus:outline-none"
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setPointsEditId(entry.id);
                      setPointsEdit(entry.points === null || entry.points === undefined ? "" : String(entry.points));
                    }}
                    className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 max-md:flex max-md:min-h-11 max-md:items-center"
                    title={t("desk_entry_points_hint")}
                    aria-label={t("desk_entry_points")}
                  >
                    <Badge
                      color={entry.points === null || entry.points === undefined ? "cyan" : "amber"}
                      className="px-1.5 py-0.5 rounded text-[10px] font-bold max-md:text-xs"
                    >
                      {entryPoints(entry)} {t("desk_points_short")}
                    </Badge>
                  </button>
                )}
                {entry.card?.client && (
                  <Badge color="blue" className="px-1.5 py-0.5 rounded text-[10px] font-semibold">
                    {entry.card.client.name}
                  </Badge>
                )}
                {entry.card?.project && (
                  <Badge color="purple" className="px-1.5 py-0.5 rounded text-[10px] font-semibold">
                    {entry.card.project.name}
                  </Badge>
                )}
                {entry.card?.clientReplied && (
                  <Badge color="emerald" className="px-1.5 py-0.5 rounded text-[10px] font-semibold">
                    {t("desk_client_replied")}
                  </Badge>
                )}
                {entry.card?.isShared && (
                  <Badge color="violet" className="px-1.5 py-0.5 rounded text-[10px] font-semibold">
                    {t("desk_shared")}
                  </Badge>
                )}
                {entry.card && (
                  <Badge color={STATUS_COLOR[entry.card.status] || "zinc"} className="px-1.5 py-0.5 rounded text-[10px] font-semibold">
                    {t(`desk_status_${entry.card.status}`)}
                  </Badge>
                )}
              </div>
            </div>
            <input
              value={noteEdits[entry.id] ?? ""}
              onChange={(e) => setNoteEdits((n) => ({ ...n, [entry.id]: e.target.value }))}
              onBlur={() => saveNote(entry)}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
              maxLength={NOTE_MAX}
              placeholder={t("desk_entry_note")}
              aria-label={t("desk_entry_note")}
              className="min-w-0 max-md:order-6 max-md:flex-1 md:w-48 rounded-lg border border-zinc-200/80 bg-transparent px-2.5 py-1.5 text-xs max-md:min-h-11 max-md:text-base focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 dark:border-zinc-800 focus:outline-none transition-all"
            />
            <button
              type="button"
              onClick={() => remove(entry)}
              className="h-7 w-7 max-md:order-3 max-md:h-11 max-md:w-11 rounded-lg flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-all ml-1 max-md:ml-0"
              title={t("desk_remove_from_plan")}
              aria-label={t("desk_remove_from_plan")}
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {entries.length > 0 && (
        <div className="mt-5 text-xs text-zinc-500 font-medium border-t border-zinc-100 dark:border-zinc-900 pt-4 flex gap-4">
          <span>{entries.length} {t("desk_planned")}</span>
          <span className="text-zinc-400">|</span>
          <span>{doneCount} {t("desk_done_lc")}</span>
          <span className="text-zinc-400">|</span>
          <span>{waitingCount} {t("desk_waiting_on_clients")}</span>
        </div>
      )}

      {activeCardNumber !== null && (
        <DeskCardDetailPanel
          cardNumber={activeCardNumber}
          companyId={companyId || ""}
          onClose={() => setActiveCardNumber(null)}
          onUpdate={load}
          onNavigate={setActiveCardNumber}
        />
      )}
    </div>
  );
}
